import React, { useState, useRef } from "react";
import DashboardLayout from "../UI/Layout/DashboardLayout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlus,
    faTrashAlt,
    faTimes,
    faImage,
    faTag,
    faHashtag,
    faLayerGroup,
    faIdBadge,
    faDollarSign,
    faCubes,
} from "@fortawesome/free-solid-svg-icons";

const emptyForm = {
    productName: "",
    productId: "",
    category: "",
    retailerId: "",
    price: "",
    stockQuantity: "",
    productImage: null,
    productImagePreview: null,
};

const REQUIRED = ["productName", "productId"];

const ManageProduct = () => {
    const [productList, setProductList] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const fileInputRef = useRef(null);

    const validate = () => {
        const errs = {};
        if (!form.productName.trim()) errs.productName = "Product Name is required.";
        if (!form.productId.trim()) errs.productId = "Product ID is required.";
        return errs;
    };

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            setForm((prev) => ({
                ...prev,
                productImage: file,
                productImagePreview: reader.result,
            }));
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveImage = () => {
        setForm((prev) => ({ ...prev, productImage: null, productImagePreview: null }));
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSave = () => {
        const errs = validate();
        if (Object.keys(errs).length) {
            setErrors(errs);
            return;
        }
        setProductList((prev) => [...prev, { ...form }]);
        handleClose();
    };

    const handleClose = () => {
        setModalOpen(false);
        setForm(emptyForm);
        setErrors({});
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const deleteProduct = (index) => {
        setProductList(productList.filter((_, i) => i !== index));
    };

    return (
        <DashboardLayout>
            <main className="content">
                <div className="container-fluid p-0">

                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="mb-0" style={{ fontWeight: 700, color: "#1a1a2e" }}>
                            Manage Products
                        </h4>
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={() => setModalOpen(true)}
                            style={{ borderRadius: 6, fontWeight: 600, padding: "7px 18px" }}
                        >
                            <FontAwesomeIcon icon={faPlus} className="mr-1" />
                            Add Product
                        </button>
                    </div>

                    {/* Product List Card */}
                    <div className="card" style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", border: "none" }}>
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="card-title mb-0" style={{ fontWeight: 700 }}>Product List</h5>
                                <span className="badge badge-secondary" style={{ fontSize: 13, background: "#6c757d", padding: "5px 12px", borderRadius: 20 }}>
                                    {productList.length} {productList.length === 1 ? "Product" : "Products"}
                                </span>
                            </div>
                            <hr style={{ marginTop: 0 }} />
                            <div className="table-responsive">
                                <table className="table table-hover table-bordered table-sm" style={{ fontSize: 13 }}>
                                    <thead style={{ background: "#1a1a2e", color: "#fff" }}>
                                        <tr>
                                            <th style={{ width: 40 }}>#</th>
                                            <th>Image</th>
                                            <th>Product Name</th>
                                            <th>Product ID</th>
                                            <th>Category</th>
                                            <th>Retailer ID</th>
                                            <th>Price</th>
                                            <th>Stock</th>
                                            <th style={{ width: 70 }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {productList.length === 0 ? (
                                            <tr>
                                                <td colSpan={9} className="text-center text-muted py-4">
                                                    <FontAwesomeIcon icon={faCubes} style={{ fontSize: 28, marginBottom: 8, display: "block", margin: "0 auto 8px" }} />
                                                    No products added yet. Click <strong>Add Product</strong> to get started.
                                                </td>
                                            </tr>
                                        ) : (
                                            productList.map((item, i) => (
                                                <tr key={i}>
                                                    <td>{i + 1}</td>
                                                    <td>
                                                        {item.productImagePreview ? (
                                                            <img
                                                                src={item.productImagePreview}
                                                                alt="product"
                                                                style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 6, border: "1px solid #dee2e6" }}
                                                            />
                                                        ) : (
                                                            <div style={{ width: 36, height: 36, background: "#f0f0f0", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                <FontAwesomeIcon icon={faImage} style={{ color: "#aaa", fontSize: 14 }} />
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ fontWeight: 600 }}>{item.productName}</td>
                                                    <td><code style={{ background: "#f4f4f4", padding: "2px 6px", borderRadius: 4 }}>{item.productId}</code></td>
                                                    <td>{item.category || <span className="text-muted">—</span>}</td>
                                                    <td>{item.retailerId || <span className="text-muted">—</span>}</td>
                                                    <td>{item.price ? `₹${item.price}` : <span className="text-muted">—</span>}</td>
                                                    <td>{item.stockQuantity || <span className="text-muted">—</span>}</td>
                                                    <td>
                                                        <button
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() => deleteProduct(i)}
                                                            style={{ borderRadius: 6 }}
                                                            title="Delete"
                                                        >
                                                            <FontAwesomeIcon icon={faTrashAlt} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Modal Overlay */}
            {modalOpen && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(10,10,30,0.55)",
                        zIndex: 1050,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backdropFilter: "blur(2px)",
                    }}
                    onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
                >
                    <div
                        style={{
                            background: "#fff",
                            borderRadius: 16,
                            width: "100%",
                            maxWidth: 560,
                            maxHeight: "90vh",
                            overflowY: "auto",
                            boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
                            animation: "slideUp 0.22s ease",
                        }}
                    >
                        {/* Modal Header */}
                        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div>
                                <h5 style={{ margin: 0, fontWeight: 700, color: "#1a1a2e", fontSize: 18 }}>Add New Product</h5>
                                <p style={{ margin: 0, fontSize: 12, color: "#888", marginTop: 2 }}>
                                    Fields marked with <span style={{ color: "#e74c3c" }}>*</span> are required
                                </p>
                            </div>
                            <button
                                onClick={handleClose}
                                style={{ background: "#f5f5f5", border: "none", borderRadius: 8, width: 34, height: 34, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#555", fontSize: 16 }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div style={{ padding: "20px 24px" }}>

                            {/* Product Image Upload */}
                            <div style={{ marginBottom: 20 }}>
                                <label style={labelStyle}>
                                    <FontAwesomeIcon icon={faImage} style={{ marginRight: 6, color: "#888" }} />
                                    Product Image
                                </label>
                                <div
                                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                    style={{
                                        border: "2px dashed #d0d7e2",
                                        borderRadius: 10,
                                        padding: "14px",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 14,
                                        background: "#fafbfc",
                                        transition: "border-color 0.2s",
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#4361ee")}
                                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#d0d7e2")}
                                >
                                    {form.productImagePreview ? (
                                        <>
                                            <img
                                                src={form.productImagePreview}
                                                alt="preview"
                                                style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 8, border: "1px solid #e0e0e0", flexShrink: 0 }}
                                            />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontWeight: 600, fontSize: 13, color: "#1a1a2e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {form.productImage?.name}
                                                </div>
                                                <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>Click to change image</div>
                                            </div>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleRemoveImage(); }}
                                                style={{ background: "#fee2e2", border: "none", borderRadius: 6, padding: "4px 8px", cursor: "pointer", color: "#e74c3c", fontSize: 12, fontWeight: 600 }}
                                            >
                                                Remove
                                            </button>
                                        </>
                                    ) : (
                                        <div style={{ display: "flex", alignItems: "center", gap: 12, width: "100%" }}>
                                            <div style={{ width: 48, height: 48, background: "#eef0f8", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                <FontAwesomeIcon icon={faImage} style={{ color: "#4361ee", fontSize: 20 }} />
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: 13, color: "#4361ee" }}>Click to upload image</div>
                                                <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>PNG, JPG, WEBP — max 5MB</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    onChange={handleImageChange}
                                />
                            </div>

                            {/* Row 1: Product Name + Product ID */}
                            <div className="row" style={{ marginBottom: 14 }}>
                                <div className="col-md-6">
                                    <label style={labelStyle}>
                                        <FontAwesomeIcon icon={faTag} style={{ marginRight: 6, color: "#888" }} />
                                        Product Name <span style={{ color: "#e74c3c" }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="e.g. Premium Headphones"
                                        value={form.productName}
                                        onChange={(e) => handleChange("productName", e.target.value)}
                                        style={errors.productName ? { ...inputStyle, borderColor: "#e74c3c", background: "#fff8f8" } : inputStyle}
                                    />
                                    {errors.productName && <div style={errorStyle}>{errors.productName}</div>}
                                </div>
                                <div className="col-md-6">
                                    <label style={labelStyle}>
                                        <FontAwesomeIcon icon={faHashtag} style={{ marginRight: 6, color: "#888" }} />
                                        Product ID <span style={{ color: "#e74c3c" }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="e.g. SKU-001"
                                        value={form.productId}
                                        onChange={(e) => handleChange("productId", e.target.value)}
                                        style={errors.productId ? { ...inputStyle, borderColor: "#e74c3c", background: "#fff8f8" } : inputStyle}
                                    />
                                    {errors.productId && <div style={errorStyle}>{errors.productId}</div>}
                                </div>
                            </div>

                            {/* Row 2: Category + Retailer ID */}
                            <div className="row" style={{ marginBottom: 14 }}>
                                <div className="col-md-6">
                                    <label style={labelStyle}>
                                        <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: 6, color: "#888" }} />
                                        Category
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="e.g. Electronics"
                                        value={form.category}
                                        onChange={(e) => handleChange("category", e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label style={labelStyle}>
                                        <FontAwesomeIcon icon={faIdBadge} style={{ marginRight: 6, color: "#888" }} />
                                        Retailer ID
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="e.g. RET-123"
                                        value={form.retailerId}
                                        onChange={(e) => handleChange("retailerId", e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                            </div>

                            {/* Row 3: Price + Stock Quantity */}
                            <div className="row" style={{ marginBottom: 6 }}>
                                <div className="col-md-6">
                                    <label style={labelStyle}>
                                        <FontAwesomeIcon icon={faDollarSign} style={{ marginRight: 6, color: "#888" }} />
                                        Price (₹)
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        placeholder="e.g. 999"
                                        min="0"
                                        value={form.price}
                                        onChange={(e) => handleChange("price", e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label style={labelStyle}>
                                        <FontAwesomeIcon icon={faCubes} style={{ marginRight: 6, color: "#888" }} />
                                        Stock Quantity
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        placeholder="e.g. 100"
                                        min="0"
                                        value={form.stockQuantity}
                                        onChange={(e) => handleChange("stockQuantity", e.target.value)}
                                        style={inputStyle}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div style={{ padding: "14px 24px 20px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={handleClose}
                                style={{ borderRadius: 8, fontWeight: 600, padding: "8px 22px", background: "#f5f5f5", border: "none", color: "#444" }}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={handleSave}
                                style={{ borderRadius: 8, fontWeight: 600, padding: "8px 28px", background: "#4361ee", border: "none" }}
                            >
                                <FontAwesomeIcon icon={faPlus} className="mr-1" />
                                Save Product
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(30px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </DashboardLayout>
    );
};

const labelStyle = {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#444",
    marginBottom: 5,
    letterSpacing: 0.2,
};

const inputStyle = {
    borderRadius: 8,
    border: "1.5px solid #e0e4ed",
    fontSize: 13,
    padding: "7px 11px",
    height: "auto",
    transition: "border-color 0.2s",
};

const errorStyle = {
    fontSize: 11,
    color: "#e74c3c",
    marginTop: 4,
    fontWeight: 500,
};

export default ManageProduct;
